import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, MapPin, User, FileText, DollarSign, Upload, Image as ImageIcon, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";

const SERVICES = [
  "Pesticide Application", "Herbicide Spraying", "Fungicide Treatment",
  "Liquid Fertilizer", "Cover Crop Seeding", "Crop Scouting & Mapping",
];

const STATUS_LABELS = {
  scheduled: "Scheduled", assigned: "Assigned", in_progress: "In Progress",
  completed: "Completed", invoiced: "Invoiced", paid: "Paid", cancelled: "Cancelled",
};

const empty = {
  client_name: "", client_phone: "", client_email: "", farm_name: "",
  service: "Pesticide Application", crop: "", acres: "",
  chemical_notes: "", scheduled_date: "", priority: "normal",
  location_address: "", location_lat: "", location_lng: "", location_notes: "",
  kml_file_url: "", assigned_pilot_id: "", quoted_amount: "",
};

export default function WorkOrderEditor() {
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [wo, setWo] = useState(null);
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState("");

  useEffect(() => {
    api.get("/admin/employees").then(({ data }) => setPilots(data.filter((p) => !p.disabled))).catch(() => {});
    if (!isNew) load();
    // eslint-disable-next-line
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/work-orders/${id}`);
      setWo(data);
      setForm({
        ...empty,
        ...data,
        acres: data.acres ?? "",
        location_lat: data.location_lat ?? "",
        location_lng: data.location_lng ?? "",
        assigned_pilot_id: data.assigned_pilot_id || "",
        quoted_amount: data.quoted_amount ?? "",
      });
      setInvoiceAmount(data.invoice_amount ?? "");
    } catch (err) {
      toast.error("Could not load work order");
      navigate("/admin/work-orders");
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const buildPayload = () => ({
    client_name: form.client_name.trim(),
    client_phone: form.client_phone.trim(),
    client_email: form.client_email?.trim() || null,
    farm_name: form.farm_name?.trim() || null,
    service: form.service,
    crop: form.crop || null,
    acres: form.acres === "" ? null : Number(form.acres),
    chemical_notes: form.chemical_notes || null,
    scheduled_date: form.scheduled_date || null,
    priority: form.priority || "normal",
    location_address: form.location_address.trim(),
    location_lat: form.location_lat === "" ? null : Number(form.location_lat),
    location_lng: form.location_lng === "" ? null : Number(form.location_lng),
    location_notes: form.location_notes || null,
    kml_file_url: form.kml_file_url || null,
    assigned_pilot_id: form.assigned_pilot_id || null,
    quoted_amount: form.quoted_amount === "" ? null : Number(form.quoted_amount),
  });

  const submit = async () => {
    if (!form.client_name || !form.client_phone || !form.location_address || !form.service) {
      toast.error("Client name, phone, service and location are required.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/work-orders", buildPayload());
        toast.success("Work order created");
        navigate(`/admin/work-orders/${data.id}`);
      } else {
        const { data } = await api.patch(`/admin/work-orders/${id}`, buildPayload());
        setWo(data);
        toast.success("Work order updated");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setSaving(false); }
  };

  const uploadKml = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, kml_file_url: data.url });
      toast.success("Map file uploaded");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    }
  };

  const invoice = async () => {
    if (!invoiceAmount || Number(invoiceAmount) <= 0) return toast.error("Enter invoice amount");
    try {
      const { data } = await api.post(`/admin/work-orders/${id}/invoice`, { invoice_amount: Number(invoiceAmount) });
      setWo(data);
      toast.success("Invoiced");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const markPaid = async () => {
    try {
      const { data } = await api.post(`/admin/work-orders/${id}/paid`);
      setWo(data);
      toast.success("Marked paid 🎉");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="min-h-screen bg-muted/30"><AdminNav /><div className="py-32 text-center"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div></div>;

  const mapsLink = form.location_lat && form.location_lng
    ? `https://www.google.com/maps?q=${form.location_lat},${form.location_lng}`
    : form.location_address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(form.location_address)}`
      : null;

  return (
    <div data-testid="wo-editor-page" className="min-h-screen bg-muted/30">
      <AdminNav />
      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="flex items-center justify-between gap-4 mb-6">
          <Button asChild variant="ghost" size="sm" className="rounded-full -ml-2">
            <Link to="/admin/work-orders"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to work orders</Link>
          </Button>
          {wo && (
            <Badge className="text-sm bg-muted text-primary border-0">
              Status: {STATUS_LABELS[wo.status] || wo.status}
            </Badge>
          )}
        </div>

        <h1 className="font-display text-3xl md:text-4xl text-primary font-bold mb-8">
          {isNew ? "New work order" : "Edit work order"}
        </h1>

        {/* Client + Service */}
        <Card title="Client & service">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FieldText label="Client name *" testid="wo-client-name" value={form.client_name} onChange={set("client_name")} />
            <FieldText label="Phone *" testid="wo-client-phone" value={form.client_phone} onChange={set("client_phone")} />
            <FieldText label="Email" testid="wo-client-email" type="email" value={form.client_email} onChange={set("client_email")} />
            <FieldText label="Farm name" testid="wo-farm-name" value={form.farm_name} onChange={set("farm_name")} />
            <FieldSelect label="Service *" testid="wo-service" value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}
              options={SERVICES.map((s) => ({ value: s, label: s }))} />
            <FieldText label="Crop" testid="wo-crop" value={form.crop} onChange={set("crop")} placeholder="Avocado, Tomato..." />
            <FieldText label="Acres" testid="wo-acres" type="number" min="0" step="0.5" value={form.acres} onChange={set("acres")} />
            <FieldSelect label="Priority" testid="wo-priority" value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}
              options={[{ value: "normal", label: "Normal" }, { value: "rush", label: "Rush (same-day)" }]} />
            <FieldText label="Scheduled date" testid="wo-date" type="date" value={form.scheduled_date || ""} onChange={set("scheduled_date")} />
            <FieldText label="Quoted amount ($)" testid="wo-quoted" type="number" min="0" step="10" value={form.quoted_amount} onChange={set("quoted_amount")} />
          </div>
          <div className="mt-5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Chemical / job notes</Label>
            <Textarea data-testid="wo-chemical-notes" value={form.chemical_notes || ""} onChange={set("chemical_notes")} rows={3} className="mt-2 rounded-xl bg-background" placeholder="Product, mix rate, target pest..." />
          </div>
        </Card>

        {/* Location */}
        <Card title="Location" icon={MapPin}>
          <FieldText label="Address *" testid="wo-address" value={form.location_address} onChange={set("location_address")} placeholder="123 Krome Ave, Homestead FL 33031" />
          <div className="grid grid-cols-2 gap-5 mt-5">
            <FieldText label="GPS latitude" testid="wo-lat" type="number" step="any" value={form.location_lat} onChange={set("location_lat")} placeholder="25.4687" />
            <FieldText label="GPS longitude" testid="wo-lng" type="number" step="any" value={form.location_lng} onChange={set("location_lng")} placeholder="-80.4776" />
          </div>
          <div className="mt-5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Location notes</Label>
            <Textarea data-testid="wo-loc-notes" value={form.location_notes || ""} onChange={set("location_notes")} rows={2} className="mt-2 rounded-xl bg-background" placeholder="Gate code, access road, hazards..." />
          </div>
          <div className="mt-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Field map (KML / KMZ / PDF / image)</Label>
              <div className="mt-2 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-input bg-background hover:bg-muted cursor-pointer text-sm font-semibold" data-testid="wo-kml-upload">
                  <Upload className="h-4 w-4" /> Upload file
                  <input type="file" accept=".kml,.kmz,.pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadKml(e.target.files[0])} />
                </label>
                {form.kml_file_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${form.kml_file_url}`} target="_blank" rel="noopener noreferrer"
                    data-testid="wo-kml-link" className="text-sm text-primary hover:text-accent inline-flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" /> View attached <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
            {mapsLink && (
              <Button asChild variant="outline" className="rounded-full">
                <a href={mapsLink} target="_blank" rel="noopener noreferrer" data-testid="wo-maps-link">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open in Google Maps
                </a>
              </Button>
            )}
          </div>
        </Card>

        {/* Assignment */}
        <Card title="Assigned pilot" icon={User}>
          <FieldSelect
            label="Pilot"
            testid="wo-pilot"
            value={form.assigned_pilot_id || "__none__"}
            onValueChange={(v) => setForm({ ...form, assigned_pilot_id: v === "__none__" ? "" : v })}
            options={[
              { value: "__none__", label: "— Unassigned —" },
              ...pilots.map((p) => ({ value: p.id, label: `${p.name} · ${p.email}`, testid: `wo-pilot-option-${p.id}` })),
            ]}
          />
          <p className="text-xs text-muted-foreground mt-2">
            {pilots.length === 0
              ? <>No pilots yet. <Link to="/admin/employees" className="text-accent underline">Create one</Link> to assign jobs.</>
              : `Assigning a pilot automatically moves the job to "Assigned" status.`}
          </p>
        </Card>

        {/* Report (if any) */}
        {wo?.report && (
          <Card title="Pilot field report" icon={FileText}>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Check-in" value={wo.check_in_at ? new Date(wo.check_in_at).toLocaleString() : "—"} />
              <Info label="Check-out" value={wo.check_out_at ? new Date(wo.check_out_at).toLocaleString() : "—"} />
              <Info label="Acres sprayed" value={wo.report.acres_sprayed ?? "—"} />
              <Info label="Chemical used" value={wo.report.chemical_used || "—"} />
              <Info label="Weather" value={wo.report.weather || "—"} />
              <Info label="Notes" value={wo.report.notes || "—"} wide />
            </div>
            {wo.photo_urls?.length > 0 && (
              <div className="mt-5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Photos ({wo.photo_urls.length})</Label>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {wo.photo_urls.map((u, i) => (
                    <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noopener noreferrer">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`Photo ${i + 1}`} className="h-28 w-full object-cover rounded-xl border border-border" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {wo.signature_data_url && (
              <div className="mt-5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Client signature</Label>
                <img src={wo.signature_data_url} alt="Client signature" className="mt-2 h-32 bg-background border border-border rounded-xl object-contain" />
              </div>
            )}
          </Card>
        )}

        {/* Invoice / Payment */}
        {wo && ["completed", "invoiced", "paid"].includes(wo.status) && (
          <Card title="Invoice & payment" icon={DollarSign}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Invoice amount ($)</Label>
                <Input type="number" min="0" step="10" value={invoiceAmount} onChange={(e) => setInvoiceAmount(e.target.value)}
                  data-testid="wo-invoice-amount" disabled={wo.status === "paid"} className="mt-2 h-11 rounded-xl bg-background" />
                {wo.invoice_date && <p className="text-xs text-muted-foreground mt-1">Invoiced on {new Date(wo.invoice_date).toLocaleString()}</p>}
                {wo.paid_date && <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Paid on {new Date(wo.paid_date).toLocaleString()}</p>}
              </div>
              <div className="flex gap-2">
                {wo.status !== "paid" && (
                  <Button onClick={invoice} variant="outline" data-testid="wo-invoice-btn" className="rounded-full">
                    {wo.status === "completed" ? "Send invoice" : "Update invoice"}
                  </Button>
                )}
                {wo.status === "invoiced" && (
                  <Button onClick={markPaid} data-testid="wo-paid-btn" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    Mark paid
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        <div className="flex justify-end mt-8">
          <Button onClick={submit} disabled={saving} data-testid="wo-save-btn" className="rounded-full bg-primary hover:bg-secondary text-primary-foreground font-semibold h-11 px-7">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> {isNew ? "Create work order" : "Save changes"}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}

const Card = ({ title, icon: Icon, children }) => (
  <section className="bg-card border border-border rounded-3xl shadow-card p-6 md:p-8 mb-6">
    <h2 className="font-display text-xl font-bold text-primary flex items-center gap-2 mb-5">
      {Icon && <Icon className="h-5 w-5 text-accent" />} {title}
    </h2>
    {children}
  </section>
);

const FieldText = ({ label, testid, value, ...props }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">{label}</Label>
    <Input data-testid={testid} value={value ?? ""} {...props} className={`mt-2 h-11 rounded-xl bg-background ${props.className || ""}`} />
  </div>
);

const FieldSelect = ({ label, testid, value, onValueChange, options }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">{label}</Label>
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testid} className="mt-2 h-11 rounded-xl bg-background">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={o.testid}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Info = ({ label, value, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-medium text-foreground mt-0.5">{String(value)}</div>
  </div>
);
